/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ProductVelocitySettings } from '@shared/types/product-app-settings';
import type { SkillShopService } from '../../services/skillShopService';

export function useChangesSettings(skillShopService: SkillShopService) {
  const [velocitySettings, setVelocitySettings] = useState<ProductVelocitySettings>({
    autoAIConflictResolution: true,
    aiResolutionThreshold: 0.8,
    worktreeSidebarWidth: 256,
    worktreeSidebarCollapsed: false,
    changesSimpleMode: true,
  });

  const [sidebarWidth, setSidebarWidth] = useState(256);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [changesPanelWidth, setChangesPanelWidth] = useState(350);
  const [isResizingChangesPanel, setIsResizingChangesPanel] = useState(false);

  const changesPanelRef = useRef<HTMLDivElement>(null);
  const hasAutoSelectedRef = useRef<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const userSettings = await skillShopService.getUserSettings();
        const savedSettings = userSettings?.integrationSettings?.productApp?.velocitySettings;
        if (savedSettings) {
          setVelocitySettings({
            autoAIConflictResolution: savedSettings.autoAIConflictResolution ?? true,
            aiResolutionThreshold: savedSettings.aiResolutionThreshold ?? 0.8,
            worktreeSidebarWidth: savedSettings.worktreeSidebarWidth ?? 256,
            worktreeSidebarCollapsed: savedSettings.worktreeSidebarCollapsed ?? false,
            changesPanelWidth: savedSettings.changesPanelWidth ?? 350,
            changesSimpleMode: savedSettings.changesSimpleMode ?? true,
          });
          if (savedSettings.worktreeSidebarWidth) {
            setSidebarWidth(savedSettings.worktreeSidebarWidth);
          }
          if (savedSettings.worktreeSidebarCollapsed !== undefined) {
            setSidebarCollapsed(savedSettings.worktreeSidebarCollapsed);
          }
          if (savedSettings.changesPanelWidth) {
            setChangesPanelWidth(savedSettings.changesPanelWidth);
          }
        }
      } catch (error) {
        console.error('[Changes] Failed to load velocity settings:', error);
      }
    };
    loadSettings();
  }, [skillShopService]);

  const handleSidebarWidthChange = useCallback(async (width: number) => {
    setSidebarWidth(width);
    try {
      await skillShopService.updateVelocitySettings({
        ...velocitySettings,
        worktreeSidebarWidth: width,
      });
    } catch (error) {
      console.error('[Changes] Failed to save sidebar width:', error);
    }
  }, [skillShopService, velocitySettings]);

  const handleSidebarCollapsedChange = useCallback(async (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    try {
      await skillShopService.updateVelocitySettings({
        ...velocitySettings,
        worktreeSidebarCollapsed: collapsed,
      });
    } catch (error) {
      console.error('[Changes] Failed to save sidebar collapsed state:', error);
    }
  }, [skillShopService, velocitySettings]);

  const saveChangesPanelWidth = useCallback(async (width: number) => {
    try {
      await skillShopService.updateVelocitySettings({
        ...velocitySettings,
        changesPanelWidth: width,
      });
    } catch (error) {
      console.error('[Changes] Failed to save changes panel width:', error);
    }
  }, [skillShopService, velocitySettings]);

  const handleChangesPanelMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingChangesPanel(true);
  }, []);

  const handleChangesPanelMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingChangesPanel || !changesPanelRef.current) return;
    const panelRect = changesPanelRef.current.getBoundingClientRect();
    const newWidth = e.clientX - panelRect.left;
    if (newWidth >= 250 && newWidth <= 600) {
      setChangesPanelWidth(newWidth);
    }
  }, [isResizingChangesPanel]);

  const handleChangesPanelMouseUp = useCallback(() => {
    if (isResizingChangesPanel) {
      setIsResizingChangesPanel(false);
      saveChangesPanelWidth(changesPanelWidth);
    }
  }, [isResizingChangesPanel, changesPanelWidth, saveChangesPanelWidth]);

  useEffect(() => {
    if (isResizingChangesPanel) {
      document.addEventListener('mousemove', handleChangesPanelMouseMove);
      document.addEventListener('mouseup', handleChangesPanelMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleChangesPanelMouseMove);
        document.removeEventListener('mouseup', handleChangesPanelMouseUp);
      };
    }
  }, [isResizingChangesPanel, handleChangesPanelMouseMove, handleChangesPanelMouseUp]);

  return {
    velocitySettings,
    sidebarWidth,
    sidebarCollapsed,
    changesPanelWidth,
    isResizingChangesPanel,
    changesPanelRef,
    hasAutoSelectedRef,
    handleSidebarWidthChange,
    handleSidebarCollapsedChange,
    handleChangesPanelMouseDown,
  };
}
